import api from '../api';

/**
 * Client service orchestrating Face Biometric API requests with the LIMS backend.
 */
class FaceAuthService {
  /**
   * Authenticates an employee via face vector matching.
   */
  async loginWithFace({ faceEncoding, username = '', centreId = null, roleId = null } = {}) {
    const payload = {
      faceEncoding,
      username: username ? username.trim() : null,
      centreId: centreId ? parseInt(centreId, 10) : null,
      roleId: roleId ? parseInt(roleId, 10) : null,
    };

    const res = await api.post('/auth/face/login', payload);
    if (!res.data?.success || !res.data?.data) {
      throw new Error(res.data?.message || 'Face authentication verification failed.');
    }
    return res.data.data;
  }

  /**
   * Enrolls an employee's face template.
   */
  async enrollEmployeeFace(employeeId, faceEncoding, quality = 1.0) {
    const payload = {
      employeeId: parseInt(employeeId, 10),
      faceEncoding,
      quality: Number(quality) || 1.0,
    };

    const res = await api.post('/auth/face/enroll-employee', payload);
    return res.data;
  }

  /**
   * Revokes an employee's enrolled face template.
   */
  async revokeEmployeeFace(employeeId) {
    const res = await api.post(`/auth/face/revoke-employee/${employeeId}`);
    return res.data;
  }

  /**
   * Gets face enrollment status for an employee.
   */
  async getEmployeeFaceStatus(employeeId) {
    try {
      const res = await api.get(`/auth/face/status/${employeeId}`);
      return res.data?.data || { isEnrolled: false };
    } catch {
      return { isEnrolled: false };
    }
  }

  /**
   * Identifies an existing patient using 128-D face embedding vector matching.
   */
  async identifyPatientByFace(faceEncoding, limit = 5, customThreshold = null) {
    const payload = {
      faceEncoding,
      limit,
      customThreshold,
    };

    const res = await api.post('/patients/identify-by-face', payload);
    if (!res.data?.success || !res.data?.data) {
      throw new Error(res.data?.message || 'Face identification request failed.');
    }
    return res.data.data;
  }

  /**
   * Enrolls or updates patient face biometric template.
   */
  async enrollPatientFace(uhid, faceEncoding) {
    const payload = {
      patientId: uhid,
      faceEncoding,
    };

    const res = await api.post(`/patients/${uhid}/face-enroll`, payload);
    return res.data;
  }

  /**
   * Revokes a patient's enrolled face template.
   */
  async revokePatientFace(uhid) {
    const res = await api.post(`/patients/${uhid}/face-revoke`);
    return res.data;
  }
}

export const faceAuthService = new FaceAuthService();
export default faceAuthService;
